import React, { useState, useEffect } from 'react';
import { apiService } from '../../services';
import { utils } from '../../utils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface DashboardStats {
  total: number;
  concluidos: number;
  pendentes: number;
  reprovados: number;
  veiculos_inspecionados: number;
  usuarios_ativos: number;
}

interface Vehicle {
  id: number;
  placa: string;
  modelo: string;
  tipo: string;
  status: string;
  quilometragem_atual: number;
  ultima_manutencao: string;
}

interface AtividadeRecente {
  tipo: string;
  acao: string;
  usuario: string;
  data: string;
  detalhes: string;
}

interface ChecklistRecente {
  id: number;
  veiculo: string;
  motorista: string;
  tipo: string;
  data: string;
  status: string;
  itens_ok: number;
  itens_nok: number;
}



const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    total: 0,
    concluidos: 0,
    pendentes: 0,
    reprovados: 0,
    veiculos_inspecionados: 0,
    usuarios_ativos: 0
  });
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [formattedChartData, setFormattedChartData] = useState<any[]>([]);
  const [atividadesRecentes, setAtividadesRecentes] = useState<AtividadeRecente[]>([]);
  const [checklistsRecentes, setChecklistsRecentes] = useState<ChecklistRecente[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [kpis, setKpis] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(false);

  // Função para obter ícone baseado no tipo de atividade
  const getActivityIcon = (tipo: string): string => {
    switch (tipo) {
      case 'checklist':
        return 'fas fa-clipboard-check';
      case 'ocorrencia':
        return 'fas fa-exclamation-triangle';
      case 'agendamento':
        return 'fas fa-calendar-alt';
      case 'veiculo':
        return 'fas fa-truck';
      case 'motorista':
        return 'fas fa-user';
      case 'manutencao':
        return 'fas fa-wrench';
      default:
        return 'fas fa-info-circle';
    }
  };

  // Função para formatar tempo relativo
  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours} h`;
    if (diffDays < 7) return `${diffDays} d`;
    return date.toLocaleDateString('pt-BR');
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    loadChartData(selectedPeriod);
  }, [selectedPeriod]);

  useEffect(() => {
    // Formatar dados para o gráfico
    if (chartData && chartData.length > 0) {
      const formatted = chartData.map(item => ({
        data: item.data,
        total: parseInt(item.total) || 0,
        concluidos: parseInt(item.concluidos) || 0,
        reprovados: parseInt(item.reprovados) || 0
      }));
      setFormattedChartData(formatted);
    } else {
      setFormattedChartData([]);
    }
  }, [chartData]);

  const loadChartData = async (period: string) => {
    try {
      setIsChartLoading(true);
      const response = await apiService.getDashboardEvolucaoChecklists(period);
      setChartData(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados do gráfico:', error);
      utils.showNotification('Erro ao carregar dados do gráfico', { type: 'error' });
      setChartData([]);
    } finally {
      setIsChartLoading(false);
    }
  };

  const handlePeriodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    event.preventDefault();
    const period = event.target.value;
    setSelectedPeriod(period);
    loadChartData(period);
  };

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Carregar estatísticas completas do dashboard
      const dashboardResponse = await apiService.getDashboardStats('mes');
      const dashboardData = dashboardResponse.data;
      
      // Carregar atividades recentes
      const atividadesResponse = await apiService.getDashboardAtividadesRecentes(10);
      setAtividadesRecentes(atividadesResponse.data || []);
      
      // Carregar checklists recentes
      const checklistsResponse = await apiService.getChecklists({ limit: 5 });
      const checklistsData = checklistsResponse.data?.checklists || [];
      
      // Mapear checklists para o formato esperado
      const checklistsRecentesFormatados = checklistsData.map((checklist: any) => ({
        id: checklist.id,
        veiculo: checklist.veiculo_placa || 'N/A',
        motorista: checklist.motorista_nome || 'N/A',
        tipo: checklist.tipo || 'Inspeção',
        data: checklist.data_realizacao || checklist.created_at,
        status: checklist.status || 'pendente',
        itens_ok: checklist.itens_ok || 0,
        itens_nok: checklist.itens_nok || 0
      }));
      
      setChecklistsRecentes(checklistsRecentesFormatados);
      
      if (dashboardData) {
        // Atualizar estatísticas principais
        setStats({
          total: dashboardData.checklists?.total || 0,
          concluidos: dashboardData.checklists?.concluidos || 0,
          pendentes: dashboardData.checklists?.pendentes || 0,
          reprovados: dashboardData.checklists?.reprovados || 0,
          veiculos_inspecionados: dashboardData.checklists?.veiculos_inspecionados || 0,
          usuarios_ativos: dashboardData.checklists?.usuarios_ativos || 0
        });

        // Atualizar contagem de veículos
        setVehicles(Array(dashboardData.veiculos?.total_ativos || 0).fill({}));
      }

      // Carregar KPIs da frota
      try {
        const kpisResponse = await apiService.getDashboardKpis();
        if (kpisResponse.success && kpisResponse.data) setKpis(kpisResponse.data);
      } catch (kpiError) {
        console.error('Erro ao carregar KPIs:', kpiError);
      }

    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      utils.showNotification('Erro ao carregar dados do dashboard', { type: 'error' });
      
      // Fallback para dados vazios em caso de erro
      setStats({
        total: 0,
        concluidos: 0,
        pendentes: 0,
        reprovados: 0,
        veiculos_inspecionados: 0,
        usuarios_ativos: 0
      });
      setAtividadesRecentes([]);
      setChecklistsRecentes([]);
    } finally {
      setIsLoading(false);
    }
  };



  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px' }}>
        <div className="loading-spinner"></div>
        <p>Carregando dashboard...</p>
      </div>
    );
  }

  return (
    <>
      <div id="dashboard-section">
        {/* KPIs da Frota */}
        {kpis && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>KPIs da Frota</h3>
            <div className="dashboard-grid">
              <div className="stat-card" style={{ borderTop: '3px solid #3498db' }}>
                <div className="stat-icon"><i className="fas fa-road" style={{ color: '#3498db' }}></i></div>
                <div className="stat-value">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(kpis.custo_por_km)}/km</div>
                <div className="stat-label">Custo por KM</div>
              </div>
              <div className="stat-card" style={{ borderTop: '3px solid #04C64F' }}>
                <div className="stat-icon"><i className="fas fa-chart-pie" style={{ color: '#04C64F' }}></i></div>
                <div className="stat-value">{kpis.disponibilidade}%</div>
                <div className="stat-label">Disponibilidade ({kpis.veiculos_ativos}/{kpis.veiculos_total})</div>
              </div>
              <div className="stat-card" style={{ borderTop: '3px solid #f39c12' }}>
                <div className="stat-icon"><i className="fas fa-wrench" style={{ color: '#f39c12' }}></i></div>
                <div className="stat-value">{kpis.mttr_horas}h</div>
                <div className="stat-label">MTTR (Tempo Medio Reparo)</div>
              </div>
              <div className="stat-card" style={{ borderTop: '3px solid #e74c3c' }}>
                <div className="stat-icon"><i className="fas fa-money-bill-wave" style={{ color: '#e74c3c' }}></i></div>
                <div className="stat-value">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(kpis.gasto_mensal)}</div>
                <div className="stat-label">Gasto Mensal Total</div>
              </div>
              {/* Card "Consumo Medio da Frota" movido para o dashboard exclusivo
                  de abastecimentos (/dashboard-combustivel). */}
            </div>
          </div>
        )}

        {/* Statistics Cards - Exatamente como no monolito */}
        <div className="dashboard-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-clipboard-check"></i>
            </div>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Checklists Realizados</div>
            {stats.total > 0 ? (
              <div className="stat-change neutral">
                <i className="fas fa-clipboard-list"></i>
                Total acumulado
              </div>
            ) : (
              <div className="stat-change neutral">
                <i className="fas fa-minus"></i>
                Sem dados
              </div>
            )}
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-truck"></i>
            </div>
            <div className="stat-value">{vehicles.length}</div>
            <div className="stat-label">Veículos Ativos</div>
            {vehicles.length > 0 ? (
              <div className="stat-change neutral">
                <i className="fas fa-truck"></i>
                Frota cadastrada
              </div>
            ) : (
              <div className="stat-change neutral">
                <i className="fas fa-minus"></i>
                Sem dados
              </div>
            )}
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="stat-value">{stats.pendentes}</div>
            <div className="stat-label">Ocorrências Pendentes</div>
            {stats.pendentes > 0 ? (
              <div className="stat-change negative">
                <i className="fas fa-exclamation-circle"></i>
                Requerem atenção
              </div>
            ) : (
              <div className="stat-change neutral">
                <i className="fas fa-minus"></i>
                Sem dados
              </div>
            )}
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-wrench"></i>
            </div>
            <div className="stat-value">{stats.reprovados}</div>
            <div className="stat-label">Manutenções Agendadas</div>
            {stats.reprovados > 0 ? (
              <div className="stat-change neutral">
                <i className="fas fa-wrench"></i>
                Programadas
              </div>
            ) : (
              <div className="stat-change neutral">
                <i className="fas fa-minus"></i>
                Sem dados
              </div>
            )}
          </div>
        </div>

        {/* Charts Section - Igual ao monolito */}
        <div className="charts-section">
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Checklists por Período</h3>
              <div>
                <select 
                  className="form-control" 
                  style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px' }}
                  value={selectedPeriod}
                  onChange={handlePeriodChange}
                >
                  <option value="7">Últimos 7 dias</option>
                  <option value="30">Último mês</option>
                  <option value="90">Último trimestre</option>
                </select>
              </div>
            </div>
            <div className="chart-container" style={{ height: '300px', width: '100%' }}>
              {isChartLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner"></div>
                  <p>Carregando dados do gráfico...</p>
                </div>
              ) : formattedChartData && formattedChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={formattedChartData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="data" 
                      type="category"
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      allowDecimals={false}
                    />
                    <Tooltip 
                      formatter={(value) => [`${value} checklists`, 'Quantidade']}
                      labelFormatter={(label) => `Data: ${label}`}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#8884d8" 
                      activeDot={{ r: 6 }} 
                      name="Total"
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="concluidos" 
                      stroke="#82ca9d" 
                      name="Concluídos"
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="reprovados" 
                      stroke="#ff8042" 
                      name="Reprovados"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <i className="fas fa-chart-line" style={{ fontSize: '2rem', marginRight: '10px', color: '#04C64F' }}></i>
                  <div>Gráfico de Linha - Checklists Realizados</div>
                  <div style={{ marginTop: '10px', fontSize: '14px', color: '#999' }}>
                    Nenhum dado disponível para o período selecionado
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Atividades Recentes</h3>
            </div>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {atividadesRecentes.length > 0 ? (
                atividadesRecentes.map((atividade, index) => (
                  <div key={index} className="activity-item">
                    <div className={`activity-icon ${atividade.tipo}`}>
                      <i className={getActivityIcon(atividade.tipo)}></i>
                    </div>
                    <div className="activity-content">
                      <h4>{atividade.acao}</h4>
                      <p>{atividade.detalhes}</p>
                    </div>
                    <div className="activity-time">{formatTimeAgo(atividade.data)}</div>
                  </div>
                ))
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  <i className="fas fa-info-circle" style={{ fontSize: '2rem', marginBottom: '10px' }}></i>
                  <div>Nenhuma atividade recente</div>
                  <div style={{ fontSize: '14px', marginTop: '5px' }}>
                    As atividades aparecerão aqui conforme o uso do sistema
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabela de Dados - Como no monolito */}
        <div className="data-section">
          <div className="section-header">
            <h2 className="section-title">Checklists Recentes</h2>
            <button className="add-btn" onClick={() => window.location.href = '/checklists'}>
              <i className="fas fa-plus"></i>
              Novo Checklist
            </button>
          </div>
          
          <div style={{ overflowX: 'auto' }}>
            {checklistsRecentes.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Veículo</th>
                    <th>Motorista</th>
                    <th>Tipo</th>
                    <th>Data</th>
                    <th>Status</th>
                    <th>Itens OK</th>
                    <th>Itens NOK</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {checklistsRecentes.map((checklist) => (
                    <tr key={checklist.id}>
                      <td>
                        <button className="expand-btn" title="Expandir detalhes">
                          <i className="fas fa-chevron-right"></i>
                        </button>
                      </td>
                      <td>{checklist.veiculo}</td>
                      <td>{checklist.motorista}</td>
                      <td>{checklist.tipo}</td>
                      <td>{new Date(checklist.data).toLocaleDateString('pt-BR')}</td>
                      <td>
                        <span className={`status-badge status-${checklist.status}`}>
                          {checklist.status === 'concluido' ? 'Concluído' : 
                           checklist.status === 'pendente' ? 'Pendente' : 
                           checklist.status === 'reprovado' ? 'Reprovado' : checklist.status}
                        </span>
                      </td>
                      <td>{checklist.itens_ok}</td>
                      <td>{checklist.itens_nok}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-view" title="Visualizar">
                            <i className="fas fa-eye"></i>
                          </button>
                          <button className="btn-edit" title="Editar">
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn-delete" title="Excluir">
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                <i className="fas fa-clipboard-list" style={{ fontSize: '3rem', marginBottom: '15px' }}></i>
                <div style={{ fontSize: '18px', marginBottom: '10px' }}>Nenhum checklist encontrado</div>
                <div style={{ fontSize: '14px' }}>
                  Crie seu primeiro checklist para começar a usar o sistema
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
